import { Link } from 'react-router-dom'
import { cn } from '@/lib/cn'
import type { Checklist } from '../../api/lab'
import { CHECKLIST_KEYS, CHECKLIST_LABEL, EVIDENCE_LABEL, checklistLabel, evidenceLink, readableDetail } from '../../model/lab/labText'
import { Check, Code, When } from '../bits'

/** The server-computed release checklist (F7.6-b): one line per check, what it found, its evidence. */
export function ReleaseChecklistView({ checklist, highlight, compact }: { checklist: Checklist; highlight?: string[]; compact?: boolean }) {
  const order = (k: string) => {
    const i = (CHECKLIST_KEYS as readonly string[]).indexOf(k)
    return i < 0 ? 99 : i
  }
  const items = [...checklist.items].sort((a, b) => order(a.key) - order(b.key))
  return (
    <div className="flex flex-col">
      <ul className="divide-y divide-rule/70">
        {items.map((it) => (
          <li
            key={it.key}
            className={cn('flex flex-col gap-1 py-2.5', highlight?.includes(it.key) && 'rounded-chip bg-warn-wash px-2')}
          >
            <div className="flex items-start justify-between gap-3">
              <span className="min-w-0">
                <Check ok={it.ok} label={<span className="font-semibold">{checklistLabel(it.key)}</span>} />
                {!compact && CHECKLIST_LABEL[it.key] && (
                  <span className="mt-0.5 block pl-6 text-[12.5px] text-muted">{CHECKLIST_LABEL[it.key]!.hint}</span>
                )}
              </span>
              <span className={cn('shrink-0 font-mono text-[11px] font-bold uppercase tracking-label', it.ok ? 'text-ok' : 'text-danger')}>
                {it.ok ? 'OK' : 'Not OK'}
              </span>
            </div>
            {it.detail.length > 0 && (
              <ul className="flex flex-col gap-0.5 pl-6 text-[13.5px] text-ink">
                {it.detail.map((d, i) => (
                  <li key={i}>{readableDetail(d)}</li>
                ))}
              </ul>
            )}
            {it.evidence.length > 0 && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-6 text-[12.5px] text-muted">
                {it.evidence.map((e) => {
                  const to = evidenceLink(e)
                  const text = e.code ?? `${EVIDENCE_LABEL[e.type] ?? 'Record'} ${e.id.slice(0, 8)}`
                  return to ? (
                    <Link key={`${e.type}:${e.id}`} to={to} className="font-mono text-[12.5px] text-ink underline decoration-rule underline-offset-2 hover:decoration-amber">
                      {e.code ?? (EVIDENCE_LABEL[e.type] ?? 'Open')}
                    </Link>
                  ) : (
                    <Code key={`${e.type}:${e.id}`} className="text-[12.5px] text-muted">
                      {text}
                    </Code>
                  )
                })}
              </div>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[12px] text-muted">
        Checked <When at={checklist.computedAt} relative />.{' '}
        {checklist.canRelease ? 'Every line is green.' : 'Not every line is green.'}
      </p>
    </div>
  )
}
